/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-09-28 18:26:02
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Button, TextStyle } from '@shopify/polaris';
import { withI18n } from '@shopify/react-i18n';
import Lottie from 'react-lottie';
import Tour from 'reactour';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { MobileCancelMajor } from '@shopify/polaris-icons';

import PageTypeCollapse from './page-type-collapse';
import ConfirmDeleteModal from 'src/components/views/recommendation/confirm-delete-modal';
import ConfirmActionModal from 'src/components/views/recommendation/confirm-action-modal';

import {
  RECOMMENDATION_WIDGET_PAGE_TYPES,
  ACCENT_COLOR,
  CONFIG_RECOMMENDATION_WIDGETS_TOUR
} from 'src/constants/recommendation';
import { mixpanelV3Helper } from 'src/helpers/mixpanel-v3';

import step01Gif from 'src/assets/json/recommendation/widget-tour-step01.json';
import step02Gif from 'src/assets/json/recommendation/widget-tour-step02.json';
import * as recommendationAction from 'src/stores/actions/recommendation-actions';
import * as oauthAction from 'src/stores/actions/oauth-actions';

import { ComposedProps, State } from './index.type';

// Product tour config for rule page
const defaultOptions = gif => ({
  loop: true,
  autoplay: true,
  animationData: gif
});
class ListWidgets extends Component<ComposedProps, State> {
  state = {
    activeModalRemove: false,
    selectedId: '',
    widgetName: '',
    actionType: '',
    activeActionModal: false,
    openTour: false,
    currentStepTour: 0
  };

  componentWillReceiveProps(nextProps) {
    const {
      deleting,
      success,
      saving,
      saveSuccess,
      selectedTab,
      isCompletedTour,
      allWidgets = []
    } = nextProps;
    const { deleting: deletingPrev, saving: savingPrev } = this.props;
    const { selectedId } = this.state;

    // Only open tour when flag isCompletedTour = false and open draft tab
    if (selectedTab === 1 && isCompletedTour === false && allWidgets.length > 0) {
      this.setState({ openTour: true });
    }

    if (success && deleting !== deletingPrev) {
      this.handleActiveOptionModalRemove();
      this.props.afterDeleteWidget(selectedId);
      this.setState({ selectedId: '' });
    }

    if (saveSuccess && saving !== savingPrev) {
      this.setState({ activeActionModal: false });
      this.setState({ selectedId: '' });
    }
  }

  render() {
    const { openTour, currentStepTour } = this.state;
    const {
      i18n,
      saving,
      deleting,
      success,
      allWidgets,
      isApplyFilter,
      hasAddonAIPowerRecommendation,
      selectedTab,
      handleUnlock
    } = this.props;

    return (
      <div className="Polaris-Card__Section list-widgets">
        {/* overplay component for actions by vertical  when open tour*/}
        {openTour && <div data-tut="pfs_tour_actions" className="overlay-actions" />}
        {openTour && <div data-tut="pfs_tour_widgets" className="widget-by-type" />}
        <div>
          {/* Render table type content */}
          {_.map(Object.keys(RECOMMENDATION_WIDGET_PAGE_TYPES), (pageType, index) => (
            <PageTypeCollapse
              key={index}
              deleting={deleting}
              success={success}
              saving={saving}
              pageType={pageType}
              widgets={allWidgets.filter(e => e.pageType === pageType)}
              handleWidgetAction={this.handleWidgetAction}
              index={index}
              isApplyFilter={isApplyFilter}
              hasAddonAIPowerRecommendation={hasAddonAIPowerRecommendation}
              selectedTab={selectedTab}
              handleUnlock={handleUnlock}
            />
          ))}
          <ConfirmDeleteModal
            active={this.state.activeModalRemove}
            onToggleModal={this.handleActiveOptionModalRemove}
            onDelete={this.deleteWidget}
            deleting={deleting}
            title={i18n.translate('AdminHTML.Recommendation.WidgetDashboard.deleteModal_Tittle')}
            content={
              <div>
                {i18n.translate('AdminHTML.Recommendation.WidgetDashboard.deleteModal_Content1', {
                  t1: (
                    <TextStyle variation="strong">
                      {i18n.translate(
                        'AdminHTML.Recommendation.WidgetDashboard.deleteModal_Content1_t1'
                      )}
                    </TextStyle>
                  )
                })}
              </div>
            }
          />
          <ConfirmActionModal
            active={this.state.activeActionModal}
            onToggleModal={this.handleActiveActionModal}
            widgetId={this.state.selectedId}
            widgetName={this.state.widgetName}
            actionType={this.state.actionType}
            onAction={this.onActionWidget}
            loading={saving}
          />
          <Tour
            disableInteraction={true}
            disableDotsNavigation={true}
            aria-label={'Close'}
            // Render steps tour
            steps={CONFIG_RECOMMENDATION_WIDGETS_TOUR(i18n)}
            isOpen={openTour}
            className={`product-tour-step${currentStepTour}`}
            rounded={5}
            accentColor={ACCENT_COLOR}
            onAfterOpen={this.disableBody}
            onBeforeClose={this.enableBody}
            CustomHelper={this.customHelperTour}
            onRequestClose={() => this.closeTour()}
          />
        </div>
      </div>
    );
  }

  handleWidgetAction = ({ widgetId, widgetName }, type) => {
    this.setState({ selectedId: widgetId, widgetName });
    switch (type) {
      case 'duplicate': {
        this.props.duplicateWidget(widgetId);
        break;
      }
      case 'delete': {
        this.handleActiveOptionModalRemove();
        break;
      }
      case 'launch':
      case 'pause':
      case 'archive':
      case 'restore':
      default: {
        this.setState({ actionType: type });
        this.handleActiveActionModal();
      }
    }
  };

  customHelperTour = ({ current, content, totalSteps, gotoStep, close }) => {
    const { i18n, markDoneTourLoading } = this.props;
    const { currentStepTour } = this.state;
    if (currentStepTour !== current) {
      this.setState({ currentStepTour: current });
    }

    const steps = [
      {
        gif: step01Gif,
        content: i18n.translate('AdminHTML.Recommendation.WidgetTour.widgetStep01_content')
      },
      {
        gif: step02Gif,
        content: i18n.translate('AdminHTML.Recommendation.WidgetTour.widgetStep02_content', {
          t: (
            <strong variation="strong">
              {i18n.translate('AdminHTML.Recommendation.WidgetTour.widgetStep02_content_t')}
            </strong>
          )
        })
      }
    ];
    const nextButton =
      current === totalSteps - 1 ? (
        <Button loading={markDoneTourLoading} primary onClick={() => this.closeTour()}>
          {i18n.translate('AdminHTML.Common.Action.gotIt')}!
        </Button>
      ) : (
        <Button primary onClick={() => gotoStep(current + 1)}>
          {i18n.translate('AdminHTML.Common.Action.next')}
        </Button>
      );
    const prevButton =
      current > 0 ? (
        <Button onClick={() => gotoStep(current - 1)}>
          {i18n.translate('AdminHTML.Common.Action.back')}
        </Button>
      ) : (
        <></>
      );
    return (
      <div
        className={`widgets-tour widgets-tour-step${current}`}
        key={`widgets-tour-step${current}`}>
        <h2 className="heading-tour">
          <div>{i18n.translate('AdminHTML.Recommendation.WidgetTour.widgetStep01_title')}</div>
          <Button onClick={() => this.closeTour()} icon={MobileCancelMajor} plain />
        </h2>
        <div style={{ backgroundColor: '#F1F2F3' }}>
          <Lottie options={defaultOptions(steps[current].gif)} width={400} height={200} />
        </div>
        <div className="content-tour">{steps[current].content}</div>
        <div className="actions-tour">
          <div className="count">
            {current + 1} {i18n.translate('AdminHTML.Common.Common.of')} {totalSteps}
          </div>
          <div className="actions">
            {prevButton}
            {nextButton}
          </div>
        </div>
      </div>
    );
  };

  //close tour
  closeTour = () => {
    const { markDoneTourWidget, updateFeaturesAppSettings } = this.props;
    const meta = {
      onSuccess: () => {
        updateFeaturesAppSettings({ isCompletedTour: true }, 'recommendation');
        this.setState({ openTour: false });
      }
    };
    markDoneTourWidget(meta);
  };

  // Disable/enable scroll in product tour
  disableBody = target => {
    disableBodyScroll(target);
  };
  enableBody = target => enableBodyScroll(target);

  // Handle open/close modal delete
  handleActiveOptionModalRemove = () => {
    this.setState(({ activeModalRemove }) => ({
      activeModalRemove: !activeModalRemove
    }));
  };

  // Delete widget
  deleteWidget = () => {
    const { selectedId } = this.state;
    this.props.deleteRecommendationWidget(selectedId);

    // Tracking event
    const { allWidgets } = this.props;
    const deleteWidget = allWidgets.find(e => e.id === selectedId);
    mixpanelV3Helper.trackEventData('Delete Recommendation Widget', {
      feature: 'Recommendation',
      pageType: deleteWidget?.pageType,
      recommendationType: deleteWidget?.recommendationType
    });
  };

  // Handle modal action
  handleActiveActionModal = () => {
    this.setState(({ activeActionModal }) => ({
      activeActionModal: !activeActionModal
    }));
  };

  onActionWidget = () => {
    const { selectedId, actionType } = this.state;
    const { updateWidgetAction } = this.props;

    updateWidgetAction(selectedId, actionType);
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  markDoneTourWidget: meta =>
    dispatch(recommendationAction.markDoneTourWidget(meta, ownProps.i18n)),
  updateFeaturesAppSettings: (data, featureName) =>
    dispatch(oauthAction.updateFeaturesAppSettings(data, featureName))
});

const mapStateToProps = (state, props) => {
  return {
    isCompletedTour: _.get(
      state.oauth.tenantApp,
      'settings.recommendation.settings.isCompletedTour',
      null
    ),
    saving: _.get(state.recommendation, 'saving', false),
    saveSuccess: _.get(state.recommendation, 'saveSuccess', false),
    deleting: _.get(state.recommendation, 'deleting', false),
    success: _.get(state.recommendation, 'success', false),
    markDoneTourLoading: _.get(state.recommendation, 'markDoneTourLoading', false)
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(ListWidgets));
